<template>
  <form-modal
    :ref="modalName"
    :name="modalName"
    class="NewTopicFromFeed"
    title="New Topic"
    submit-name="Create"
    extra-name="Dismiss"
    full-height
    disable-tabindex
    :submit-disabled="!currentLandscape || !currentFeed"
    @submit="submit"
    @extra-action="dismissFeed"
  >
    <template v-if="currentLandscape && currentFeed">
      <div class="mb-8">
        <lm-label class="Label">Source</lm-label>
        <a :href="currentFeed.link" target="_blank" class="text-s">{{
          currentFeed.link
        }}</a>
      </div>
      <div class="flex flex-col mb-8 md:flex-row md:items-center">
        <lm-actor-select
          ref="actorSelect"
          :selected-actor="actor"
          class="mb0-important"
        >
          Add topic to:
        </lm-actor-select>
        <lm-checkbox
          v-model="priority"
          label="Important"
          class="mt-8 md:ml-8 md:mt-0"
          form-style
        />
      </div>
      <lm-input
        ref="inputTitle"
        label="Title"
        :maxlength="0"
        focus
        @submit="submitFromInput"
      />
      <lm-editor
        ref="inputEditor"
        v-model="text"
        class="mb-8"
        :optional="false"
        @submit="submitFromInput"
      />
      <lm-tags-selector
        ref="tagSelector"
        class="mb-8"
        :options="currentLandscape.tags"
        :tags="tags"
        :required="requiredTagsFeed"
      />
    </template>
  </form-modal>
</template>

<script>
import LmCheckbox from '@/components/form/LmCheckbox'
import LmLabel from '@/components/ui/LmLabel'
import {mapGetters} from 'vuex'
import {imgInHTMLResize} from './Topic/resizeImagesInHTML'
import {Modal, Events} from '@/lib/definitions'
import EventHub from '@/lib/eventHub'
import FormModal from '@/components/modals/FormModal'
import LmInput from '@/components/form/LmInput.vue'
import FormHelper from '@/components/form/formHelper'
import LmTagsSelector from '@/components/form/LmTagsSelector'
import sanitizeHtml from 'sanitize-html'
import linkifyHtml from 'linkifyjs/html'
import LmEditor from '@/components/form/LmEditor'
import LmActorSelect from '@/components/form/LmActorSelect.vue'

export default {
  name: 'ModalNewTopicFromFeed',
  components: {
    LmLabel,
    LmCheckbox,
    LmActorSelect,
    LmTagsSelector,
    FormModal,
    LmInput,
    LmEditor
  },
  data() {
    return {
      Events,
      modalName: Modal.NEW_TOPIC_FROM_FEED,
      modalRef: null,
      inputs: null,
      filePreview: null,
      priority: false,
      tags: [],
      text: '',
      bounds: '.NewTopicFromFeed',
      inEdit: null,
      actor: null
    }
  },
  computed: {
    ...mapGetters([
      'selectedActor',
      'currentLandscape',
      'currentFeed',
      'getActorByID',
      'getSourceById'
    ]),
    ...mapGetters({
      requiredTagsFeed: 'preferences/requiredTagsFeed'
    })
  },
  mounted() {
    this.modalRef = this.$refs[this.modalName]
    this.inputs = [
      this.$refs.inputTitle,
      this.$refs.inputEditor,
      this.$refs.tagSelector,
      this.$refs.actorSelect
    ]
    this.init()
  },
  methods: {
    setText(data) {
      if (!this.currentFeed) return
      let text =
        data || this.currentFeed.summary ? this.currentFeed.summary : ''
      if (
        this.currentFeed.type === 'YOUTUBE' ||
        this.currentFeed.type === 'VIMEO'
      ) {
        const iframeSrc =
          this.currentFeed.type === 'YOUTUBE'
            ? this.currentFeed.link.replace('watch?v=', 'embed/')
            : this.currentFeed.link.replace(
                'vimeo.com',
                'player.vimeo.com/video'
              )

        text += `<iframe class="iframeEditorTemp" frameborder="0" src="${iframeSrc}" ></iframe>`
      } else {
        if (this.currentFeed.image) {
          text += `<p><img width="300" src="${this.currentFeed.image}" /></p>`
        }
      }
      if (this.currentFeed.link) {
        text += `<p>Source:&nbsp;<a target="_blank" href="${this.currentFeed.link}">${this.currentFeed.link}</a></p>`
      }
      text = linkifyHtml(text)
      this.text = sanitizeHtml(text, {
        allowedTags: [
          'b',
          'i',
          'em',
          'strong',
          'a',
          'br',
          'img',
          'p',
          'blockquote',
          'iframe',
          'li',
          'ol',
          'ul'
        ],
        allowedAttributes: {
          a: ['href', 'target'],
          img: ['src'],
          iframe: ['src', 'allowfullscreen']
        }
      })
    },
    init() {
      if (!this.currentFeed) return
      if (this.inEdit !== this.currentFeed._id) {
        this.inEdit = this.currentFeed._id
        this.actor = this.$store.getters.getActorByID(this.currentFeed.actorId)
        const source = Array.isArray(this.currentFeed.source)
          ? this.getSourceById(this.currentFeed.source[0])
          : undefined
        this.tags = this.currentFeed.tags
        this.$refs.inputTitle.data = this.currentFeed.title
        if (source) {
          setTimeout(() => {
            this.$store
              .dispatch('loadFullFeed', {
                feedId: this.currentFeed._id,
                source: this.currentFeed.source
              })
              .then(data => {
                this.setText(data.description)
              })
          }, 500)
        } else {
          this.setText(this.currentFeed.summary)
        }
      }
    },
    submitFromInput() {
      this.modalRef.submit()
    },
    submit() {
      if (FormHelper.validate(this.inputs)) {
        imgInHTMLResize(this.text).then(resizedImgsText => {
          // Call API
          let payload = {
            id: this.currentFeed._id,
            title: this.$refs.inputTitle.data,
            text: resizedImgsText,
            priority: this.priority,
            tags: this.$refs.tagSelector.data,
            actorId: this.$refs['actorSelect'].data,
            feedId: this.currentFeed.source,
            guid: this.currentFeed.guid,
            mentions: FormHelper.getEditorMentions(),
            pubdate: this.currentFeed.pubdate
          }
          this.$store
            .dispatch('feedToTopic', payload)
            .then(newTopic => {
              EventHub.emit(
                Events.SHOW_SNACKBAR,
                'Topic created',
                'View Topic',
                this.viewTopic,
                newTopic._id
              )
              this.modalRef.submitSuccess()
            })
            .catch(error => this.modalRef.submitFails(error))
        })
      } else {
        this.modalRef.submitFails()
      }
    },
    viewTopic(topicId) {
      this.$router.pushQuery({topic: topicId})
    },
    dismissFeed() {
      const payload = {
        feedId: this.currentFeed._id,
        source: this.currentFeed.source,
        guid: this.currentFeed.guid
      }
      this.$store.dispatch('dismissFeed', payload).then(() => {
        EventHub.emit(
          Events.SHOW_SNACKBAR,
          'Feed dismissed',
          'undo',
          this.undismissFeed,
          payload
        )
      })
      this.modalRef.close()
    },
    undismissFeed(payload) {
      this.$store.dispatch('undismissFeed', payload)
      EventHub.emit(Events.SHOW_SNACKBAR, 'Feed restored')
    }
  }
}
</script>

<style scoped>
.Label {
  margin-bottom: 8px !important;
}
</style>
